import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {projectDeployedEquipmentKeys} from '../../project-deployed-equipment/query-keys'
import {getJob} from '../api'
import {mapOperationalJobDetail} from '../job-api-adapter'
import {jobKeys} from '../query-keys'
import {applyStockEquipmentAssignmentDelta, getAssignableStockEquipment} from './api'
import {stockEquipmentKeys} from './query-keys'
import type {ApplyStockEquipmentAssignmentDeltaInput} from './schemas'

export function useAssignableStockEquipment(
    jobId: string,
    rentalAssetTypeId: string | null | undefined,
    options?: {enabled?: boolean},
) {
    return useQuery({
        queryKey: stockEquipmentKeys.assignable(jobId, rentalAssetTypeId ?? ''),
        queryFn: () => getAssignableStockEquipment(jobId, rentalAssetTypeId!),
        enabled: (options?.enabled ?? true) && Boolean(jobId && rentalAssetTypeId),
        staleTime: 0,
    })
}

export function useApplyStockEquipmentAssignmentDelta(jobId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (input: ApplyStockEquipmentAssignmentDeltaInput) => {
            const summary = await applyStockEquipmentAssignmentDelta(jobId, input)
            const job = mapOperationalJobDetail(await getJob(jobId))
            return {summary, job}
        },
        onError: (_error, input) => {
            void queryClient.invalidateQueries({
                queryKey: stockEquipmentKeys.assignable(jobId, input.rentalAssetTypeId),
            })
        },
        onSuccess: ({summary, job}, input) => {
            void queryClient.invalidateQueries({
                queryKey: stockEquipmentKeys.assignable(jobId, input.rentalAssetTypeId),
            })
            void queryClient.invalidateQueries({
                queryKey: jobKeys.detail(jobId),
            })
            if (job.projectId) {
                void queryClient.invalidateQueries({
                    queryKey: projectDeployedEquipmentKeys.detail(job.projectId),
                })
            }
            if (!input.thirdPartyRentalAssetOfferingId)
                queryClient.setQueryData(
                    stockEquipmentKeys.assignable(jobId, summary.rentalAssetTypeId),
                    summary,
                )
        },
    })
}
